<template>
  <v-autocomplete
    ref="input"
    color="black"
    v-model="chosenTract"
    :label="$t('tract')"
    :clearable="clearable && !readonly"
    @click:clear="$emit('tract-cleared')"
    :loading="tractsLoading"
    :items="eligibleTracts"
    item-value="tractId"
    return-object
    :filter="filterTract"
    :disabled="disabled"
    :readonly="readonly"
    :tabindex="readonly ? -1 : undefined"
    :append-icon="readonly ? '' : '$dropdown'"
    @click="readonly ? $event.stopPropagation() : null"
    @mousedown="readonly ? $event.preventDefault() : null"
    :search-input.sync="search"
    :class="{ 'bid-tract-collapse-input': chosenTract && !search }"
    id="bid-tract-autocomplete">
    <template #prepend-inner>
      <v-icon>mdi-crosshairs-gps</v-icon>
    </template>
    <template #item="{ item }">
      <v-list-item-content>
        <v-list-item-title>{{ item.name }}</v-list-item-title>
        <v-list-item-subtitle v-if="item.code">{{ item.code }}</v-list-item-subtitle>
      </v-list-item-content>
      <v-list-item-action-text v-if="item.distanceFromOrigin !== undefined">
        {{ $t('distanceInMiles', { distance: item.distanceFromOrigin.toFixed(1) }) }}
      </v-list-item-action-text>
    </template>
    <template #selection="{ item }">
      <span>{{ item.name }}<span v-if="item.code" class="grey--text text--darken-1 ml-2">{{ item.code }}</span></span>
    </template>
  </v-autocomplete>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { TractFilter } from '@/model/Tract.js'
import { milesBetween } from '@/utils/Distance.js'

export default {
  name: 'BidTractAutocomplete',

  props: {
    propTractId: { type: Number, default: undefined },
    tractFilter: { type: Function, default: null },
    clearable: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    originSpot: { type: Object, default: null }
  },

  data: () => ({
    chosenTract: undefined,
    search: null
  }),

  computed: {
    ...mapGetters('tract', ['allTracts', 'tractsLoading']),

    baseTracts () {
      return this.tractFilter ? this.allTracts.filter(this.tractFilter) : this.allTracts
    },

    eligibleTracts () {
      const tracts = this.chosenTract && !this.baseTracts.some(bt => bt.tractId === this.chosenTract.tractId)
        ? this.baseTracts.concat(this.chosenTract)
        : this.baseTracts

      if (typeof this.originSpot?.latitude !== 'number' || typeof this.originSpot?.longitude !== 'number') return tracts

      return tracts
        .map(t => ({ ...t, distanceFromOrigin: milesBetween(this.originSpot, t.spot) }))
        .sort((a, b) => (a.distanceFromOrigin ?? Infinity) - (b.distanceFromOrigin ?? Infinity))
    }
  },

  watch: {
    propTractId (val) {
      if (!val) this.chosenTract = undefined
    },

    chosenTract (val, oldVal) {
      if (val?.tractId !== oldVal?.tractId && val?.tractId !== this.propTractId) this.$emit('tract-chosen', val)
      this.$nextTick(() => { this.search = null })
    }
  },

  created () {
    this.refreshTracts()
  },

  methods: {
    ...mapActions('tract', ['fetchTracts', 'fetchTract']),

    focus () {
      if (this.$refs.input) this.$refs.input.focus()
    },

    async refreshTracts () {
      await this.fetchTracts(new TractFilter({
        includeInitiated: true,
        includeCruised: true,
        includeExecuted: true,
        includeActive: true,
        includeClosed: true,
        includeArchived: true
      }))

      if (this.propTractId !== undefined && this.propTractId !== null) {
        const found = this.baseTracts.find(t => t.tractId === this.propTractId)
        this.chosenTract = found ?? await this.fetchTract(this.propTractId)
      }
    },

    filterTract (item, queryText) {
      const query = queryText.toLowerCase()
      return item.name.toLowerCase().includes(query) || (item.code || '').toLowerCase().includes(query)
    }
  }
}
</script>

<style scoped>
.bid-tract-collapse-input ::v-deep .v-select__selections input {
  width: 0;
  min-width: 0;
  flex: 0 0 0;
}

.bid-tract-collapse-input.v-input--is-focused ::v-deep .v-select__selections input {
  min-width: 0;
}
</style>
